/**
 * Shared spreadsheet parsing for the import flows (products and sales).
 *
 * Pure by design — no expo/react-native imports — so it can be unit-tested and so the
 * platform-specific file reading stays in utils/importFileReader.ts.
 *
 * Output shape is the array-of-arrays the product import already consumes: row 0 is the
 * header row, every later row is one data row. `rowNumbers[i]` is the 1-based row number
 * of `rows[i]` in the SOURCE file, which is what the sales import quotes back to the user
 * when a row is invalid — blank rows are dropped from `rows`, so the array index alone
 * would point at the wrong line in their spreadsheet.
 */

import Papa from 'papaparse'
import * as XLSX from 'xlsx'

export type ImportCell = string | number | boolean | null
export type ImportRows = ImportCell[][]

export type ImportFileKind = 'csv' | 'tsv' | 'xlsx'

export interface ImportSheet {
  /** Header row first, then data rows. Fully blank rows removed, ragged rows padded. */
  rows: ImportRows
  /** 1-based source row number for each entry of `rows`. */
  rowNumbers: number[]
  /**
   * Workbook uses the 1904 date system (legacy Mac Excel). Date serials in such a file
   * are 1462 days behind the 1900 system, so the date parser has to know.
   */
  date1904: boolean
}

export const SUPPORTED_IMPORT_EXTENSIONS = ['csv', 'tsv', 'xlsx', 'xls'] as const

export const importFileKindFromName = (name: string): ImportFileKind | null => {
  const extension = (name || '').split('.').pop()?.toLowerCase()
  if (extension === 'csv') return 'csv'
  if (extension === 'tsv') return 'tsv'
  if (extension === 'xlsx' || extension === 'xls') return 'xlsx'
  return null
}

const isBlankCell = (cell: ImportCell): boolean =>
  cell === null || cell === undefined || (typeof cell === 'string' && cell.trim() === '')

const isBlankRow = (row: ImportCell[]): boolean => row.every(isBlankCell)

const normalizeCell = (cell: unknown): ImportCell => {
  if (cell === undefined || cell === null) return null
  if (typeof cell === 'string' || typeof cell === 'number' || typeof cell === 'boolean') return cell
  // Date cells only appear when a caller enables cellDates; keep them addressable as text
  // rather than leaking a Date into a shape the rest of the pipeline types as primitives.
  if (cell instanceof Date) return cell.toISOString()
  return String(cell)
}

/**
 * Drop fully blank rows, pad ragged rows to the widest row, and record source row numbers.
 * `firstRowNumber` is the 1-based number of `rawRows[0]` in the file.
 */
export const normalizeImportRows = (rawRows: unknown[][], firstRowNumber = 1): ImportSheet => {
  const rows: ImportRows = []
  const rowNumbers: number[] = []

  rawRows.forEach((rawRow, index) => {
    const row = (Array.isArray(rawRow) ? rawRow : []).map(normalizeCell)
    if (isBlankRow(row)) return
    rows.push(row)
    rowNumbers.push(firstRowNumber + index)
  })

  const width = rows.reduce((max, row) => Math.max(max, row.length), 0)
  rows.forEach((row) => {
    while (row.length < width) row.push(null)
  })

  return { rows, rowNumbers, date1904: false }
}

/**
 * A delimited file whose quoting is broken. Papaparse does not stop at an unclosed quote: it keeps
 * reading and folds every later line into that one cell, so the import would see one bad row and
 * quietly lose the rest of the file. A TSV written without quoting (a script joining cells with
 * tabs) does this for any cell that starts with `"`, e.g. a product named `"Крым" 25кг`. The file
 * is refused instead, naming the row, so nothing below it is silently dropped.
 */
export class MalformedImportFileError extends Error {
  constructor(public row: number) {
    super(`Malformed quotes in import file at row ${row}`)
    this.name = 'MalformedImportFileError'
  }
}

/**
 * Parse delimited text. `skipEmptyLines` is intentionally OFF: blank lines are removed by
 * normalizeImportRows instead, which keeps `rowNumbers` aligned with the real file.
 * `delimiter` undefined lets papaparse detect it (the CSV path, as before).
 *
 * Only quote errors refuse the file. Papaparse also reports `UndetectableDelimiter` for a file
 * with one column or one data row, which it then parses correctly with the default comma.
 */
const parseDelimitedText = (text: string, delimiter: string | undefined): ImportSheet => {
  const result = Papa.parse<unknown[]>(text, {
    header: false,
    dynamicTyping: true,
    skipEmptyLines: false,
    ...(delimiter !== undefined ? { delimiter } : {}),
  })
  const quoteError = (result.errors || []).find((error) => error.type === 'Quotes')
  if (quoteError) {
    // `row` is the index of the parsed row the quote opened in, the same count rowNumbers uses.
    throw new MalformedImportFileError((quoteError.row ?? 0) + 1)
  }
  return normalizeImportRows(result.data || [])
}

/** Parse CSV text. */
export const parseCsvText = (text: string): ImportSheet => parseDelimitedText(text, undefined)

/**
 * Parse tab-separated text (ATL-1901). The delimiter is fixed rather than guessed: a TSV cell may
 * hold a comma ("0,5", "Пупырка 0,6м"), which delimiter detection could take for the separator.
 */
export const parseTsvText = (text: string): ImportSheet => parseDelimitedText(text, '\t')

/**
 * Parse a workbook's first sheet.
 *
 * `raw: true` and no `cellDates` on purpose: SheetJS builds Date objects using the
 * runtime's local timezone, which shifts a date-only cell across midnight for negative
 * UTC offsets. Date cells therefore arrive as Excel serial numbers and are decoded by
 * utils/salesImport/parseRows, which is timezone-independent and testable.
 */
export const parseWorkbookData = (
  data: ArrayBuffer | Uint8Array | string,
  type: 'array' | 'base64' | 'binary'
): ImportSheet => {
  const workbook = XLSX.read(type === 'array' ? new Uint8Array(data as ArrayBuffer) : data, { type })
  const sheetName = workbook.SheetNames[0]
  if (!sheetName) {
    return { rows: [], rowNumbers: [], date1904: false }
  }
  const sheet = workbook.Sheets[sheetName]
  const rawRows = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    raw: true,
    blankrows: true,
    defval: null,
  })

  // sheet_to_json starts at the sheet's used range, which need not be row 1.
  let firstRowNumber = 1
  if (sheet['!ref']) {
    firstRowNumber = XLSX.utils.decode_range(sheet['!ref']).s.r + 1
  }

  const sheetResult = normalizeImportRows(rawRows, firstRowNumber)
  return {
    ...sheetResult,
    date1904: Boolean(workbook.Workbook?.WBProps?.date1904),
  }
}
